import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import Loader from "../../../components/Common/Loader";
import "../../../assets/styles/PccountsPage.css";
import { commonService } from "../../../services/commonService";

interface DataPoint {
    id: number;
    header_name: string;
    status: string;
    createdBy: string;
    updatedBy: string;
    createdAt: string;
    updatedAt: string;
}

const ManageDataPointsPage: React.FC = () => {
    const navigate = useNavigate();

    const [headerInput, setHeaderInput] = useState("");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState("");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
    const [message, setMessage] = useState<{ status: "success" | "error"; text: string } | null>(null);
    const [confirmModal, setConfirmModal] = useState<{
        id: number;
        newStatus: "Active" | "Inactive";
    } | null>(null);
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

    useEffect(() => {
        fetchDataPoints();
    }, []);
    const capitalizeFirstLetter = (value?: string | null) => {
        if (!value) return "-";
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    };
    // ✅ Fetch & Transform API Data
    const fetchDataPoints = async () => {
        try {
            setLoading(true);
            const response = await commonService.cdget();

            const formattedData = (response?.data || []).map((item: any) => ({
                id: item.id,
                header_name: item.header_name?.toUpperCase() || "-",
                status: item.is_deleted ? "Inactive" : "Active",
                createdBy: capitalizeFirstLetter(item.created_by),
                updatedBy: capitalizeFirstLetter(item.updated_by),
                createdAt: item.created_at
                    ? new Date(item.created_at).toLocaleDateString()
                    : "-",
                updatedAt: item.updated_at
                    ? new Date(item.updated_at).toLocaleDateString()
                    : "-",
            }));

            setDataPoints(formattedData);
        } catch (error) {
            console.error("Error fetching data points:", error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Add Header (UI only for now)
    const handleAddHeader = async () => {
        if (!headerInput.trim()) return;
        setCreating(true);
        const newData = {
            header_name: headerInput,
        };
        try {
            const response = await commonService.cdCreate(newData);
            setHeaderInput("");
            fetchDataPoints(); // Refresh data points list
            setMessage({
                status: "success",
                text: "Header added successfully."
            });
        } catch (error: any) {
            console.log("Error adding header:", error);
            const backendMessage =
                error?.response?.message ||   // Axios standard
                error?.message ||
                "Failed to add header. Please try again.";

            setMessage({
                status: "error",
                text: backendMessage
            });
        } finally {
            setCreating(false);
        }

    };

    useEffect(() => {
        if (!message) return;

        const timer = setTimeout(() => {
            setMessage(null);
        }, 5000);

        return () => clearTimeout(timer); // cleanup
    }, [message?.text]);

    const handleEdit = (id: number, value: string) => {
        setEditingId(id);
        setEditingValue(value);
    };

    const handleSave = async (id: number) => {
        const trimmedValue = editingValue.trim();
        if (!trimmedValue) {
            setMessage({ status: "error", text: "Header name cannot be empty." });
            return;
        }

        const existingItem = dataPoints.find(dp => dp.id === id);
        if (!existingItem) return;

        // ✅ 1️⃣ Check if value is unchanged (case-insensitive)
        if (trimmedValue.toLowerCase() === existingItem.header_name.toLowerCase()) {
            setEditingId(null);
            setEditingValue("");
            return;
        }
        const duplicateExists = dataPoints.some(
            dp =>
                dp.id !== id && // ignore current row
                dp.header_name.toLowerCase() === trimmedValue.toLowerCase()
        );

        if (duplicateExists) {
            setMessage({
                status: "error",
                text: "Header already exists."
            });
            return;
        }
        try {
            await commonService.cdUpdate(id, { header_name: trimmedValue });
            setDataPoints(prev =>
                prev.map(item =>
                    item.id === id
                        ? {
                            ...item,
                            header_name: editingValue.trim().toUpperCase(),
                        }
                        : item
                )
            );
        } catch (error: any) {

        } finally {
            setEditingId(null);
            setEditingValue("");
        }

    };

    const handleCancel = () => {
        setEditingId(null);
        setEditingValue("");
    };

    const handleConfirmStatusChange = async () => {
        if (!confirmModal) return;

        const { id, newStatus } = confirmModal;

        const existingItem = dataPoints.find(dp => dp.id === id);
        if (!existingItem) return;

        try {
            await commonService.cdUpdate(id, {
                header_name: existingItem.header_name,
                is_deleted: newStatus === "Inactive"
            });

            setDataPoints(prev =>
                prev.map(item =>
                    item.id === id
                        ? { ...item, status: newStatus }
                        : item
                )
            );

            setMessage({
                status: "success",
                text: `Header ${newStatus === "Inactive" ? "disabled" : "enabled"} successfully.`
            });

        } catch (error: any) {
            setMessage({
                status: "error",
                text:
                    error?.response?.data?.message ||
                    "Failed to update status."
            });
        } finally {
            setConfirmModal(null);
        }
    };

    const handleToggleStatus = (id: number) => {
        setDataPoints(
            dataPoints.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        status:
                            item.status === "Active"
                                ? "Inactive"
                                : "Active",
                    }
                    : item
            )
        );
    };

    useEffect(() => {
        const handleClickOutside = () => {
            setOpenDropdownId(null);
        };

        if (openDropdownId !== null) {
            window.addEventListener("click", handleClickOutside);
        }

        return () => {
            window.removeEventListener("click", handleClickOutside);
        };
    }, [openDropdownId]);

    const dropdownItemStyle: React.CSSProperties = {
        padding: "8px 12px",
        cursor: "pointer",
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    };

    return (
        <MainLayout>
            <div className="pc-counts-page custom-scrollbar">
                <main className="pc-counts-main">

                    {/* 🔙 Back Button */}
                    <div className="pc-counts-header">
                        <button
                            onClick={() => navigate(-1)}
                            className="pc-counts-back-button"
                        >
                            <BackIcon />
                            Back
                        </button>
                    </div>

                    <div className="pc-counts-dashboard">

                        {/* Title */}
                        <div className="pc-counts-dashboard-header">
                            <h2 className="pc-counts-dashboard-title">
                                Manage Data Points
                            </h2>
                        </div>

                        {/* ✅ ADD HEADER FORM */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                marginBottom: "25px",
                            }}
                        >
                            <div style={{
                                background: "#ffffff",
                                padding: "20px 30px",
                                borderRadius: "10px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",

                                gap: "15px",
                                alignItems: "center",
                            }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "15px",
                                    }}
                                >
                                    {/* <label style={{ fontWeight: 600 }}>
                                        Header Name:
                                    </label> */}

                                    <input
                                        type="text"
                                        value={headerInput}
                                        onChange={(e) =>
                                            setHeaderInput(e.target.value)
                                        }
                                        placeholder="Enter header name"
                                        style={{
                                            padding: "6px 10px",
                                            borderRadius: "6px",
                                            border: "1px solid #ccc",
                                            width: "250px",
                                        }}
                                    />

                                    <button
                                        disabled={!headerInput.trim()}
                                        onClick={handleAddHeader}
                                        style={{
                                            background: "#61A1D4",
                                            color: "#fff",
                                            border: "none",
                                            padding: "6px 15px",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {!creating ? "Add" : "Adding..."}
                                    </button>

                                </div>
                                <div className="mt-3" style={{ marginLeft: "20px", textAlign: "center" }}>
                                    {message?.text && (
                                        <span style={{ color: message.status === "success" ? "green" : "red" }}>
                                            {message.text}
                                        </span>
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* ✅ TABLE */}
                        <div className="pc-counts-table-wrapper" style={{overflowY:"unset",maxHeight:"unset"}}>
                            <table className="pc-counts-table" style={{ marginBottom: "0" }}>
                                <thead>
                                    <tr>
                                        <th>Sl. No.</th>
                                        <th>Header Name</th>
                                        <th>Status</th>
                                        <th>Created By</th>
                                        <th>Updated By</th>
                                        <th>Created At</th>
                                        <th>Updated At</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={8} style={{ textAlign: "center" }}>
                                                <Loader text="Loading..." />
                                            </td>
                                        </tr>
                                    ) : dataPoints.length > 0 ? (
                                        dataPoints.map((item, index) => (
                                            <tr key={item.id}>
                                                <td data-label="Sl. No.">{index + 1}</td>

                                                {/* Header */}
                                                <td data-label="Header Name">
                                                    {editingId === item.id ? (
                                                        <>
                                                            <input
                                                                value={editingValue}
                                                                onChange={(e) =>
                                                                    setEditingValue(
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                            <button style={{ fontSize: "10px" }} onClick={() => handleSave(item.id)}>✔️</button>
                                                            <button style={{ fontSize: "10px" }} onClick={handleCancel}>❌</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {item.header_name}{" "}
                                                            <button
                                                                style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "10px" }}
                                                                onClick={() =>
                                                                    handleEdit(
                                                                        item.id,
                                                                        item.header_name
                                                                    )
                                                                }
                                                            >
                                                                ✏️
                                                            </button>
                                                        </>
                                                    )}
                                                </td>

                                                <td data-label="Status"><span style={{ color: "white", background: item.status === "Active" ? "#05910587" : "#ab1d1d82", width: "min-content", padding: "2px 4px", borderRadius: "5px" }}>{item.status}</span></td>
                                                <td data-label="Created By">{item.createdBy}</td>
                                                <td data-label="Updated By">{item.updatedBy}</td>
                                                <td data-label="Created At">{item.createdAt}</td>
                                                <td data-label="Updated At">{item.updatedAt}</td>
                                                <td data-label="Action" style={{ position: "relative" }}>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenDropdownId(openDropdownId === item.id ? null : item.id);
                                                        }}
                                                        style={{
                                                            background: "#f1f3f5",
                                                            border: "1px solid #ccc",
                                                            padding: "6px 12px",
                                                            borderRadius: "6px",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "6px"
                                                        }}
                                                    >
                                                        ⚙ Action ▾
                                                    </button>

                                                    {openDropdownId === item.id && (
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: "38px",
                                                                right: "0",
                                                                background: "#fff",
                                                                border: "1px solid #ccc",
                                                                borderRadius: "6px",
                                                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                                                width: "170px",
                                                                zIndex: 1000
                                                            }}
                                                        >
                                                            
                                                            {/* Enable / Disable */}
                                                            <div
                                                                style={{
                                                                    ...dropdownItemStyle,
                                                                    color: item.status === "Active" ? "red" : "green"
                                                                }}
                                                                onClick={() => {
                                                                    setOpenDropdownId(null);
                                                                    setConfirmModal({
                                                                        id: item.id,
                                                                        newStatus:
                                                                            item.status === "Active"
                                                                                ? "Inactive"
                                                                                : "Active"
                                                                    });
                                                                }}
                                                            >
                                                                {item.status === "Active" ? "🚫 Disable" : "🔓 Enable"}
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={8} style={{ textAlign: "center" }}>
                                                No records found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </main>
            </div>
            {confirmModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0,0,0,0.4)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        background: "#fff",
                        padding: "20px 30px",
                        borderRadius: "8px",
                        width: "300px",
                        textAlign: "center"
                    }}>
                        <p>
                            Are you sure you want to{" "}
                            <strong>
                                {confirmModal.newStatus === "Inactive"
                                    ? "Disable"
                                    : "Enable"}
                            </strong>{" "}
                            this header?
                        </p>

                        <div style={{ display: "flex", justifyContent: "space-around", marginTop: "15px" }}>
                            <button
                                onClick={handleConfirmStatusChange}
                                style={{
                                    background: "#61A1D4",
                                    color: "#fff",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "5px"
                                }}
                            >
                                Confirm
                            </button>

                            <button
                                onClick={() => setConfirmModal(null)}
                                style={{
                                    background: "#ccc",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "5px"
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
};

export default ManageDataPointsPage;